import React from "react";
import { Link } from "react-router-dom";

import { faHotel } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import Avatar from "~/ui/legacy/Avatar";
import { ContactSupportLink } from "~/ui/legacyFellowIndex";

import { AuthBase } from "../AuthBase";
import { Button, Input } from "../components/";
import { ReactComponent as EmailSent } from "../images/email-sent.svg";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";

import styles from "../css/Auth.module.css";

interface LoginGuestState {
	email: string;
	emailSent: boolean;
	provider: "google" | "office365" | "unknown" | undefined;
	errorMessage: string | undefined;
}

type LoginGuestResult =
	| {
			success: true;
			provider: "google" | "office365" | "unknown";
	  }
	| {
			success: false;
			message: string;
	  };

export default class LoginGuest extends AuthBase<AuthProps, LoginGuestState> {
	state: LoginGuestState = {
		email: "",
		emailSent: false,
		provider: undefined,
		errorMessage: undefined,
	};

	documentTitle = "Guest Login";

	getPaneComponent() {
		return WhitelabelPane;
	}

	onEmailKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.sendMagicLink();
		}
	};

	onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ email: event.target.value });
	};

	onClearEmail = () => {
		this.setState({ email: "" });
	};

	sendMagicLink = async () => {
		const { email } = this.state;
		if (!email) return;

		const token = await recaptcha("login_guest");

		const data = new FormData();
		data.append("email", email);
		data.append("captcha_token", token);

		const response = await fetch("/auth/guest/login/", {
			method: "POST",
			body: data,
			headers: {
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		}).catch(() => {
			dispatchAnalyticsTrack("Guest Login Step Failed", { feature: "Authentication", resolved: false });
			this.setState({ errorMessage: "Something went wrong! Please contact our support team." });
		});

		if (!response) return;
		const result: LoginGuestResult = await response.json();

		if (result.success) {
			dispatchAnalyticsTrack("Guest Login Step Complete", { feature: "Authentication", resolved: true });
			this.setState({ emailSent: true, errorMessage: "", provider: result.provider });
		} else {
			dispatchAnalyticsTrack("Guest Login Step Failed", { feature: "Authentication", resolved: false });
			this.setState({ errorMessage: result.message });
		}
	};

	renderAccountLogo = () => {
		const { account } = window.INITIAL_STATE;

		if (account) {
			return (
				<Avatar
					circle
					link={false}
					width="7.5"
					user={{ id: "0", fullName: account.name, image: account.logo }}
					style={{ margin: "auto" }}
					className={styles.workspaceLogo}
				/>
			);
		}

		return (
			<div className={cx(styles.placeholder, styles.workspaceLogo)}>
				<FontAwesomeIcon icon={faHotel} />
			</div>
		);
	};

	renderEmailSent() {
		const { email, provider } = this.state;

		let header: React.ReactNode = "Check your inbox";
		let inboxLink = "";

		if (provider === "google" || provider === "office365") {
			if (provider === "google") {
				inboxLink = `https://mail.google.com/mail/u/${email}/#search/from:fellow.app+in:anywhere`;
			} else if (provider === "office365") {
				inboxLink = `https://outlook.office.com/mail/inbox/${email}/`;
			}

			header = (
				<>
					<u>
						<a href={inboxLink}>Click here</a>
					</u>
					<br />
					to check your inbox
				</>
			);
		}

		return (
			<div>
				<EmailSent />
				<h2>{header}</h2>
				<h5 className={styles.emailSent}>
					If there is an account that matches that email, we will send you a login link to this workspace. If you
					experience difficulties,{" "}
					<ContactSupportLink message="Hi! I can't seem to find my workspace.">contact us</ContactSupportLink>.
				</h5>

				{this.props.history.length > 1 && (
					<div className={styles.backButton} onClick={this.onBackClick}>
						<FontAwesomeIcon icon={faArrowLeft} />
						<span>Back</span>
					</div>
				)}
			</div>
		);
	}

	renderStep() {
		const { subdomain, account } = window.INITIAL_STATE;
		if (!subdomain || !account) return null;

		const { email, emailSent } = this.state;
		if (emailSent) return this.renderEmailSent();

		return (
			<div className={styles.authFlex}>
				{this.renderAccountLogo()}
				<h2>Log in to {account.name} </h2>

				<h4>Enter your email</h4>
				<div className={styles.guestLoginInput}>
					<Input
						value={email}
						placeholder="me@example.com"
						onKeyDown={this.onEmailKeydown}
						onChange={this.onEmailChange}
						onClear={this.onClearEmail}
					/>
				</div>

				<div className={styles.authFormNav}>
					<Button primary disabled={!email || emailSent} onClick={this.sendMagicLink}>
						Confirm
					</Button>
				</div>
			</div>
		);
	}

	renderFooter() {
		return <Link to="/auth/login/">Log in with Single sign-on</Link>;
	}
}
